import type {ItemId} from '@github-ui/board-core'
import {useResizeObserver} from '@github-ui/use-resize-observer'
import {defaultRangeExtractor, type Range, useVirtualizer, type VirtualItem} from '@github-ui/virtualizer'
import {
  type RefCallback,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

/**
 * The block size a card is taken to be until it has been measured.
 *
 * **Measured from this board's own card**, rather than borrowed from another product. A
 * `BoardCardFrame` drawn the way this package's own Storybook fixtures draw one -- a title
 * line and a reference line -- reports a 55px border box, at a 16px root font size, in the
 * 22rem column the board states. The `--stack-gap-condensed` between two cards is handed
 * to the window separately as its gap, so this is one card and nothing else. The
 * measurement is in `window-cards.browser.test.tsx`, which asserts this number against a
 * rendered card so it cannot quietly drift from the stylesheet.
 *
 * Comparable boards record something nearer 125px, and the gap between the two figures is
 * content rather than disagreement: their cards carry labels, assignees and a repository
 * line, and this package draws none of those, because what a card holds is the caller's.
 * 55 is what can honestly be measured here.
 *
 * **It governs one frame of one cell.** Every card that is drawn is measured, and a cell
 * that has measured anything at all reserves its unseen cards at the average of what it
 * has already shown rather than at this. That average is the honest number, because what
 * a card holds is the caller's: this package's own two-line card and a rich one are out
 * by a factor of three from each other, and no constant can be right for both.
 *
 * **Erring low is the safe direction** for the frame this does govern. An estimate under
 * the truth makes that frame draw a few more cards than it needed; one over it leaves a
 * band of nothing at the bottom of the cell until the measurements arrive.
 */
export const ESTIMATED_CARD_BLOCK_SIZE = 55

/**
 * How many cards either side of the cell's viewport stay drawn.
 *
 * Four, where the board's own axes overscan by one. An item there is a whole column or a
 * whole swimlane and costs a strip of cells; an item here is one card, so the same
 * insurance against a fast scroll outrunning a render is worth buying several times over.
 */
const OVERSCAN = 4

/** The measured cards' heights by card, and their mean, or `undefined` before any is measured. */
type CardSizes = {byId: ReadonlyMap<ItemId, number>; average: number | undefined}

const NO_SIZES: CardSizes = {byId: new Map(), average: undefined}

/**
 * How many cards a cell draws before its window has measured anything.
 *
 * A window needs a scroll element before it can answer, and it has none on the first
 * render or on a server. Drawing every card in that pass is the cost this removes, and
 * drawing none would send markup with an empty cell in it, so a cell draws this many and
 * stops. Sixteen is a little over a thousand pixels of them, which is deeper than the band
 * any swimlaned board puts a cell in; an unswimlaned cell can be taller, and pays one frame
 * of drawing too few before the real window replaces this.
 */
const UNMEASURED_CARDS = 16

/**
 * One thing a cell's card list draws: a card, or the reserved height of the cards between
 * two of them.
 *
 * A window over a list laid out in ordinary flow has to say where the gaps are, not just
 * where the ends are. The cards it draws are usually one run, and a reservation at each end
 * is enough for that; but the card the focus position names is drawn wherever it is, so a
 * reader who has scrolled away from it leaves the list with a hole in the middle, and the
 * reservation either side of that card is what keeps every other card where the scrollbar
 * says it is.
 */
export type BoardCardSlot =
  | {type: 'card'; index: number}
  /** `before` is the index of the card this sits in front of, which is the count at the end. */
  | {type: 'space'; size: number; before: number}

export type UseBoardCardWindowOptions = {
  /**
   * The cell's own scroll container, which is the box this window measures against. A cell
   * scrolls its cards independently of its neighbours, so each has its own.
   */
  bodyRef: RefObject<HTMLElement | null>
  /** The ids of the cards the cell has loaded, in order. The window draws their indexes. */
  itemIds: readonly ItemId[]
  /**
   * Where the board's focus position sits in `itemIds`, or `null` when it is elsewhere.
   *
   * **Must be an index into `itemIds` as they are this render**, which is what
   * `useFocusedCardIndex` settles rather than assumes. A window handed an index the cell no
   * longer has would be asked to draw an item it has no measurement for, and the caller
   * reads an offset off whatever comes back.
   *
   * The window draws this card whatever the scroll offset says. See
   * {@link useBoardCardWindow} for why that is the whole of how focus reaches a card
   * nobody has scrolled to.
   */
  focusedIndex: number | null
  /**
   * Focus has been asked for on the card at {@link UseBoardCardWindowOptions.focusedIndex}
   * and nothing has taken it yet, which is the only thing this window moves the cell for.
   */
  isFocusPending: boolean
  /**
   * Where the card being moved sits in `itemIds`, or `null` when it is not in this cell.
   *
   * Drawn whatever the scroll offset says, like the focused card, so the cards after it can
   * close up behind it and its slot stays where the cell's room for it starts. The cards that
   * close up into view from below the window are drawn too.
   */
  heldIndex?: number | null
  /**
   * Where the cell keeps room for the card being moved, as an index into `itemIds`, or `null`
   * when it keeps none. The cards on either side of it are drawn whatever the scroll offset
   * says, so the room stays drawn, and so are the cards it moves down into view from above.
   */
  roomIndex?: number | null
  /**
   * The moved card's height in pixels, for a room in a cell the card is not in. A cell that
   * holds the card measures it itself.
   */
  heldCardSize?: number
}

export type BoardCardWindowResult = {
  /** What the cell's list draws this frame, in order. */
  slots: readonly BoardCardSlot[]
  /** For the list element itself, whose stated gap the window counts in. */
  listRef: RefCallback<HTMLElement>
  /** For each card, which registers it for measurement. Needs `data-index` on the same element. */
  measureRef: (node: Element | null) => (() => void) | undefined
}

/**
 * A virtual window over one cell's cards: the third of the board's windows, inside each
 * cell the other two left in.
 *
 * ## How focus reaches a card that is not drawn
 *
 * A card outside the window is not in the page, so keyboard focus can no longer be "focus
 * that element" -- and a window that simply dropped the rest would break the keyboard in a
 * way that looks like the focus framework failing rather than the window. It does not,
 * because the two agree on one number.
 *
 * Focus is a position held as identities, and `useFocusedCardIndex` settles it to an index
 * into the same `itemIds` this window draws from. `rangeExtractor` then draws that index
 * whether or not the scroll offset asks for it, so the element the position names exists
 * for as long as its cell does. Landing focus on it is already solved: `useRovingFocus`
 * holds an outstanding focus request until the element it names renders and takes focus at
 * that point.
 *
 * Showing it is the one part the browser cannot finish on its own. Focusing an element
 * reveals it, which is enough for a cell that was already on screen, and not enough for one
 * that mounts owing focus: see {@link useOwedScroll} for why, and for what moves the cell
 * instead.
 *
 * Drawing the position rather than merely reaching it is what keeps the rest of the board
 * working. A focused card the window unmounted would take the board's only tab stop out of
 * the page with it, and the root would answer by taking the stop back, so the arrow keys
 * would stop moving a position that is still perfectly well held.
 *
 * ## Why the cards are measured here rather than by the virtualizer
 *
 * `@tanstack/react-virtual` will measure an element for you, and this does not let it. Its
 * observer writes the new size and re-renders inside the `ResizeObserver` callback itself,
 * which is the pattern `@github-ui/eslint-plugin-web-vitals` has a rule against: a
 * synchronous update there reflows during the layout phase and stalls presentation, and in
 * a cell of five hundred cards it produced "ResizeObserver loop completed with undelivered
 * notifications" on the board's own tests.
 *
 * So the sizes come from `@github-ui/use-resize-observer` instead -- one observer for every
 * card, coalesced to a frame, bailing when a size has not changed -- and reach the window
 * through `estimateSize`, which is the one channel a size travels down. A card that has
 * been measured answers with its own height and one that has not answers with the estimate
 * above, so the arithmetic is exact for everything on screen and an estimate only for what
 * nobody has seen.
 */
export function useBoardCardWindow({
  bodyRef,
  itemIds,
  focusedIndex,
  isFocusPending,
  heldIndex = null,
  roomIndex = null,
  heldCardSize = 0,
}: UseBoardCardWindowOptions): BoardCardWindowResult {
  const count = itemIds.length
  const {listRef, gap} = useCardGap()
  const {measureRef, sizes} = useCardSizes(itemIds)

  const getScrollElement = useCallback(() => bodyRef.current, [bodyRef])

  // Keyed on the card rather than on its index, so a measurement follows the card it was
  // taken from. Paging only ever appends, but a card closed or moved elsewhere shifts every
  // index after it, and an index-keyed store would hand each of those cards the height of
  // its neighbour.
  const estimateSize = useCallback(
    (index: number) => {
      const id = itemIds[index]
      const measured = id === undefined ? undefined : sizes.byId.get(id)

      // A card that has been measured answers with its own height. One nobody has seen
      // answers with the average of the cards this cell has already shown, because the
      // best guide to a card of the caller's is another card of the caller's: what a card
      // holds is theirs, and a board of two-line cards and a board of rich ones are out
      // by a factor of three from each other. The constant is only for a cell that has
      // measured nothing at all, which is one frame.
      return measured ?? sizes.average ?? ESTIMATED_CARD_BLOCK_SIZE
    },
    // A new `estimateSize` is a new `rangeExtractor`, which is what makes the virtualizer extract
    // its indexes again: it caches them by that function and the ends of the range.
    [itemIds, sizes],
  )

  // The virtualizer picks cards by where they are laid out, and a held card's slot can close,
  // which moves every card after it up by its height and a gap, while a room opening moves the
  // cards from it on down by the same. The cards either moves into view are drawn as well.
  const rangeExtractor = useCallback(
    (range: Range) => {
      const sizeAt = (index: number) => estimateSize(index) + gap
      let reach = heldIndex === null ? range : extendRange(range, estimateSize(heldIndex) + gap, sizeAt)
      if (roomIndex !== null && (heldIndex === null || roomIndex < heldIndex)) {
        const roomSize = heldIndex === null ? heldCardSize : estimateSize(heldIndex)
        reach = extendRangeBack(reach, roomSize + gap, sizeAt)
      }
      const besideRoom =
        roomIndex === null ? [] : [roomIndex - 1, roomIndex].filter(index => index >= 0 && index < count)
      return pinIndexes(defaultRangeExtractor(reach), focusedIndex, heldIndex, ...besideRoom)
    },
    [count, estimateSize, focusedIndex, gap, heldCardSize, heldIndex, roomIndex],
  )

  const view = useVirtualizer<HTMLElement, HTMLElement>({
    count,
    getScrollElement,
    estimateSize,
    rangeExtractor,
    gap,
    overscan: OVERSCAN,
  })

  // Hands a changed size to a virtualizer that would otherwise never ask for it, the same
  // way the board's own axes do.
  useResolvedSizes(view.measure, sizes, itemIds)

  useOwedScroll({scrollToIndex: view.scrollToIndex, focusedIndex, isFocusPending, drawn: view.items.length})

  const fallback = useFallback(count, focusedIndex, heldIndex)
  const slots = useMemo(
    () => (view.items.length === 0 ? fallback : toSlots(view.items, view.totalSize, gap, count)),
    [view.items, view.totalSize, gap, count, fallback],
  )

  return {slots, listRef, measureRef}
}

/**
 * Moves the cell to the card focus is owed to, once the window can place it.
 *
 * **The card window's share of the same job `BoardWindowFocus` does for the board's own two
 * axes**, and the one thing that could not be left to the browser. Focusing an element
 * reveals it, so an ordinary move needs nothing here: the card is already drawn where the
 * scrollbar says it is, and taking focus scrolls the cell to it. A cell that mounts owing
 * focus is the case that is different in kind. `useRovingFocus` focuses in a layout effect,
 * and a card's effect runs before its cell's, so the browser scrolls against the screenful
 * a window with no scroll element yet draws -- a layout with none of the reserved height in
 * it, and one that is replaced before anything is painted. The offset it chose describes a
 * cell that no longer exists, and the card ends up holding focus thousands of pixels below
 * what the reader can see.
 *
 * So the index is latched when focus is asked for and spent when the window has really
 * drawn something, which is the frame in which an offset is worth anything. Only for a
 * pending request, never for the position alone: see `useIsCardFocusPending`.
 */
function useOwedScroll({
  scrollToIndex,
  focusedIndex,
  isFocusPending,
  drawn,
}: {
  scrollToIndex: (index: number) => void
  focusedIndex: number | null
  isFocusPending: boolean
  drawn: number
}): void {
  const owedRef = useRef<number | null>(null)

  useLayoutEffect(() => {
    if (isFocusPending && focusedIndex !== null) owedRef.current = focusedIndex

    const owed = owedRef.current
    if (owed === null || drawn === 0) return

    owedRef.current = null
    // `auto` alignment, so a card the cell is already showing is left exactly where it is.
    scrollToIndex(owed)
  }, [isFocusPending, focusedIndex, drawn, scrollToIndex])
}

/**
 * Tells a virtualizer that a size it already asked for has changed.
 *
 * `estimateSize` is the only channel a size travels down, and the measurements are memoised
 * on the count, the gap, the scroll margin and the virtualizer's own cache version -- never
 * on `estimateSize` itself. So a card that has just been measured would be computed and
 * then ignored, and `measure` is what moves that version. Before paint, because the
 * alternative is a frame drawn at the old size.
 *
 * On the ids as well as on the sizes, because a card removed from the middle of a cell
 * changes which size every index after it answers with, and no card resized to report it.
 */
function useResolvedSizes(measure: () => void, sizes: CardSizes, itemIds: readonly ItemId[]): void {
  useLayoutEffect(() => {
    measure()
  }, [measure, sizes, itemIds])
}

/**
 * Every drawn card's measured height, keyed by the card rather than by where it sits.
 *
 * One shared observer for all of them, coalesced to a frame and de-duplicated per element,
 * which is what keeps a cell of five hundred cards to one render per frame rather than one
 * per card. The window is told a copy of the sizes, taken only when one has changed, so
 * a card that resized to the size it already was costs nothing at all.
 */
function useCardSizes(itemIds: readonly ItemId[]): {
  measureRef: (node: Element | null) => (() => void) | undefined
  sizes: CardSizes
} {
  const sizesRef = useRef<Map<ItemId, number>>(new Map())
  const [sizes, setSizes] = useState<CardSizes>(NO_SIZES)

  // The mean height of the cards this cell has measured, kept as a running total rather
  // than worked out from the map. `estimateSize` is asked about every index the window
  // considers, so walking the map there would be quadratic in a cell of five hundred.
  const totalRef = useRef(0)
  const measuredRef = useRef(0)
  const averageRef = useRef<number | undefined>(undefined)

  // What a card was as wide as when it was last measured. Every card in a cell is the same
  // width, so one number covers them all.
  const inlineSizeRef = useRef(0)

  // Read at callback time rather than closed over, because the observer's handler outlives
  // the render that attached it. Written in a layout effect, which runs in the commit that
  // drew the cards; the handler runs in an animation frame after that, so what it reads is
  // always the array the elements in the page were drawn from.
  const itemIdsRef = useRef(itemIds)
  useLayoutEffect(() => {
    itemIdsRef.current = itemIds
  }, [itemIds])

  // The map lives as long as the cell, and a cell that has been scrolled end to end has an
  // entry per card. Released with the cell rather than left to the ref.
  useEffect(() => {
    const measured = sizesRef.current

    return () => {
      measured.clear()
    }
  }, [])

  const measureRef = useResizeObserver(entry => {
    const index = Number(entry.target.getAttribute('data-index'))
    const id = itemIdsRef.current[index]
    if (id === undefined) return

    const box = entry.borderBoxSize[0]
    const size = box?.blockSize ?? entry.contentRect.height

    // A card's height is a fact about its width: the same title wraps to one line in a wide
    // column and three in a narrow one. So a column that has changed width invalidates every
    // height taken at the old one, including the cards nobody can see, which have no
    // observer entry of their own to correct them and would otherwise keep claiming a size
    // they lost. Read off the card that is reporting rather than from a second observer,
    // because every card in a cell is the same width.
    const inlineSize = box?.inlineSize ?? entry.contentRect.width
    if (inlineSize !== inlineSizeRef.current) {
      inlineSizeRef.current = inlineSize
      sizesRef.current.clear()
      // The average is made of those measurements, so it goes with them. Keeping it would
      // reserve every unseen card at a height taken at a width the column no longer has.
      totalRef.current = 0
      measuredRef.current = 0
      averageRef.current = undefined
    } else if (sizesRef.current.get(id) === size) {
      // Bail when it has not moved, so a coalesced frame cannot drive another resize into
      // render into resize.
      return
    }

    const previous = sizesRef.current.get(id)
    if (previous === undefined) measuredRef.current += 1
    else totalRef.current -= previous

    totalRef.current += size
    averageRef.current = measuredRef.current === 0 ? undefined : totalRef.current / measuredRef.current

    sizesRef.current.set(id, size)
    setSizes({byId: new Map(sizesRef.current), average: averageRef.current})
  })

  return {measureRef, sizes}
}

/**
 * The gap the list states between two cards, in pixels.
 *
 * A virtualizer counts in pixels and the board states the gap as a token, so a browser is
 * asked to resolve it, off the list element itself rather than off a copy of the rule. It
 * is read as the element is attached, and once: the value is a spacing token, so nothing
 * about the size of a cell or of the board moves it, which is the same thing the board's
 * own two axes assume about theirs.
 *
 * Zero until it has been read, which is one frame, and the frame in which the window has no
 * scroll element either and so draws its fallback regardless.
 */
function useCardGap(): {listRef: RefCallback<HTMLElement>; gap: number} {
  const [gap, setGap] = useState(0)

  const listRef = useCallback<RefCallback<HTMLElement>>(node => {
    if (node === null) return

    // A ref callback runs in the commit phase, so this reads a list the browser has already
    // laid out, and it settles on the first frame rather than after one.
    const rowGap = Number.parseFloat(getComputedStyle(node).rowGap)
    setGap(Number.isFinite(rowGap) ? rowGap : 0)
  }, [])

  return {listRef, gap}
}

/**
 * What a cell draws before its window can answer: a screenful, never the whole cell.
 *
 * Plus the card the focus position names and the card being moved, wherever they are, for
 * the same reason `rangeExtractor` keeps them. Otherwise a cell remounting -- on the server,
 * on the first paint, or as its column scrolls back into the board's own window -- would
 * spend a frame with the board's only tab stop missing, and the root answers a missing tab
 * stop by taking it back. No reservation goes with them, because this branch makes no claim
 * about size at all.
 */
function useFallback(count: number, focusedIndex: number | null, heldIndex: number | null): readonly BoardCardSlot[] {
  const drawn = Math.min(count, UNMEASURED_CARDS)
  // Only when they are outside what the screenful already covers, and only when the cell
  // really holds them.
  const focusedPin = focusedIndex !== null && focusedIndex >= drawn && focusedIndex < count ? focusedIndex : null
  const heldPin = heldIndex !== null && heldIndex >= drawn && heldIndex < count ? heldIndex : null

  return useMemo(() => {
    const slots = Array.from({length: drawn}, (_, index): BoardCardSlot => ({type: 'card', index}))
    const pins = [...new Set([focusedPin, heldPin])].filter((index): index is number => index !== null)
    for (const index of pins.sort((a, b) => a - b)) slots.push({type: 'card', index})

    return slots
  }, [drawn, focusedPin, heldPin])
}

/**
 * The indexes a window draws, plus the ones it has to draw wherever they are.
 *
 * Sorted, because the caller lays these out in ordinary flow and reads the gaps between
 * them off this order.
 */
/** `range` with enough cards after its end to fill `size` pixels, each `sizeAt` pixels tall. */
function extendRange(range: Range, size: number, sizeAt: (index: number) => number): Range {
  let endIndex = range.endIndex
  let filled = 0
  while (filled < size && endIndex < range.count - 1) {
    endIndex += 1
    filled += sizeAt(endIndex)
  }

  return endIndex === range.endIndex ? range : {...range, endIndex}
}

/** `range` with enough cards before its start to fill `size` pixels, each `sizeAt` pixels tall. */
function extendRangeBack(range: Range, size: number, sizeAt: (index: number) => number): Range {
  let startIndex = range.startIndex
  let filled = 0
  while (filled < size && startIndex > 0) {
    startIndex -= 1
    filled += sizeAt(startIndex)
  }

  return startIndex === range.startIndex ? range : {...range, startIndex}
}

function pinIndexes(indexes: number[], ...pinned: ReadonlyArray<number | null>): number[] {
  if (indexes.length === 0) return indexes

  const missing = [...new Set(pinned)].filter((index): index is number => index !== null && !indexes.includes(index))
  if (missing.length === 0) return indexes

  return [...indexes, ...missing].sort((a, b) => a - b)
}

/**
 * The cards this frame, and the reserved height of the ones between them.
 *
 * Every reservation gives up the gaps the list draws around it, because a spacer is a child
 * of the same flex container the cards are and is spaced from its neighbours exactly as
 * they are from each other. One gap at either end of the list, where the spacer has a
 * neighbour on one side only, and two in the middle, where it has one on each. A
 * reservation that works out to nothing is the same statement as leaving no cards out, so
 * it is not drawn at all.
 */
function toSlots(
  items: readonly VirtualItem[],
  totalSize: number,
  gap: number,
  count: number,
): readonly BoardCardSlot[] {
  const slots: BoardCardSlot[] = []
  // `null` rather than `0`: the start of the list is not a card's end, and the difference is
  // exactly one gap.
  let previousEnd: number | null = null

  for (const item of items) {
    const size = item.start - (previousEnd ?? 0) - (previousEnd === null ? gap : gap * 2)
    if (size > 0) slots.push({type: 'space', size, before: item.index})

    slots.push({type: 'card', index: item.index})
    previousEnd = item.end
  }

  if (previousEnd !== null) {
    const size = totalSize - previousEnd - gap
    if (size > 0) slots.push({type: 'space', size, before: count})
  }

  return slots
}
